import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect } from 'react';

import * as AppPropTypes from '../../lib/PropTypes';

import { useTheme, useThemeClassName } from '../../contexts/ThemeContext';
import NumberField from '../fields/Number';

import styles from '../../../styles/questions/choices-input.module.scss';

const propTypes = {
    className: PropTypes.string,
    question: AppPropTypes.question,
    answer: AppPropTypes.answer,
    defaultAnswer: PropTypes.number,
    defaultMinValue: PropTypes.number,
    defaultMaxValue: PropTypes.number,
    onAnswer: PropTypes.func,
};

const defaultProps = {
    className: null,
    question: null,
    answer: null,
    defaultAnswer: 0,
    defaultMinValue: 0,
    defaultMaxValue: 99,
    onAnswer: null,
};

function NhlChoicesInputQuestion({
    className,
    question,
    answer,
    defaultAnswer,
    defaultMinValue,
    defaultMaxValue,
    onAnswer,
}) {
    const { name: theme = null } = useTheme();
    const themeClassName = useThemeClassName(styles);
    const {
        choices,
        validAnswer = null,
        defaultAnswer: finalDefaultAnswer = defaultAnswer,
        minValue: finalDefaultMinValue = defaultMinValue,
        maxValue: finalDefaultMaxValue = defaultMaxValue,
    } = question || {};
    const { value = null } = answer || {};
    const hasValidAnswer = validAnswer !== null;

    useEffect(() => {
        if (value !== null) {
            return () => {};
        }
        let timeout = setTimeout(() => {
            if (onAnswer !== null) {
                onAnswer(
                    choices.reduce(
                        (all, { id }) => ({ ...all, [id]: `${finalDefaultAnswer}` }),
                        {},
                    ),
                );
            }
        }, 0);

        return () => {
            clearTimeout(timeout);
            timeout = null;
        };
    }, [onAnswer, choices, finalDefaultAnswer]);

    const onChoiceChange = useCallback(
        (id, inputValue) => {
            if (onAnswer !== null) {
                onAnswer({ ...value, ...{ [id]: inputValue } });
            }
        },
        [onAnswer, value],
    );

    const onInputFocus = useCallback(
        ({ target, id, choiceValue }) => {
            if (`${choiceValue}` === `${finalDefaultAnswer}`) {
                if (onAnswer !== null) {
                    onAnswer({ ...value, ...{ [id]: '' } });
                }
            } else {
                target.select();
            }
        },
        [value, finalDefaultAnswer, onAnswer],
    );

    const onInputBlur = useCallback(
        ({ id, choiceValue }) => {
            if (choiceValue === '') {
                if (onAnswer !== null) {
                    onAnswer({ ...value, ...{ [id]: `${finalDefaultAnswer}` } });
                }
            }
        },
        [value, finalDefaultAnswer, onAnswer],
    );

    return (
        <div
            className={classNames([
                styles.container,
                themeClassName,
                {
                    [className]: className !== null,
                },
            ])}
        >
            <ul className={styles.choices}>
                {(choices || []).map(({ id, label, image }, choiceIndex) => {
                    const { url: imageUrl = null, alt: imageAlt } = image || {};
                    const { [id]: choiceValue } = value || {};
                    const { [id]: validChoiceAnswer } = validAnswer || {};
                    const isChoiceValidAnswer = `${validChoiceAnswer}` === `${choiceValue}`;
                    return (
                        <li
                            key={`choice-${choiceIndex + 1}`}
                            className={classNames([
                                styles.choice,
                                {
                                    [styles.validated]: hasValidAnswer,
                                    [styles.validAnswer]: isChoiceValidAnswer,
                                },
                            ])}
                        >
                            <div className={styles.choiceInner}>
                                {imageUrl !== null ? (
                                    <img className={styles.image} src={imageUrl} alt={imageAlt} />
                                ) : null}
                                <span className={styles.label}>{label}</span>
                                <NumberField
                                    className={styles.input}
                                    value={`${choiceValue || ''}`}
                                    min={finalDefaultMinValue}
                                    max={finalDefaultMaxValue}
                                    pattern="[0-9]*"
                                    onFocus={(e) => {
                                        onInputFocus({ target: e.currentTarget, id, choiceValue });
                                    }}
                                    onBlur={(e) => {
                                        onInputBlur({ target: e.currentTarget, id, choiceValue });
                                    }}
                                    onChange={(inputValue) => {
                                        onChoiceChange(id, inputValue);
                                    }}
                                />
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

NhlChoicesInputQuestion.propTypes = propTypes;
NhlChoicesInputQuestion.defaultProps = defaultProps;

export default NhlChoicesInputQuestion;
